import { MouseEvent, useState } from "react";
import { autoUpdate, flip, offset, shift, useClick, useDismiss, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { MITFloatingPortal } from "src/componentsUtils/PortalHelpers";
import "./PLAAlertMark.css";

type Props = {
   messages: string[];
   placement?: "top" | "right" | "bottom" | "left";
};

export function PLAAlertMark({ messages, placement = "top" }: Props) {
   const [open, setOpen] = useState(false);
   const { refs, floatingStyles, context } = useFloating({
      whileElementsMounted: autoUpdate,
      open,
      onOpenChange: setOpen,
      placement,
      strategy: "fixed",
      middleware: [
         offset(8),
         flip({ padding: 8, fallbackPlacements: ["right", "left", "bottom", "top"] }),
         shift({ padding: 8 }),
      ],
   });
   const { getReferenceProps, getFloatingProps } = useInteractions([
      useHover(context, { move: false, delay: { open: 120, close: 80 } }),
      useClick(context),
      useDismiss(context),
   ]);

   if (!messages.length) return null;

   return (
      <>
         <span
            ref={refs.setReference}
            className="pla-alert-mark"
            aria-label={messages.join(". ")}
            {...getReferenceProps({
               onClick: (event: MouseEvent) => event.stopPropagation(),
               onPointerDown: (event: MouseEvent) => event.stopPropagation(),
            })}
         >
            !
         </span>
         {open ? (
            <MITFloatingPortal>
               <div
                  ref={refs.setFloating}
                  className="pla-alert-tooltip"
                  role="tooltip"
                  style={floatingStyles}
                  {...getFloatingProps()}
               >
                  {messages.length === 1 ? (
                     messages[0]
                  ) : (
                     <ul>
                        {messages.map((message, index) => (
                           <li key={`${index}-${message}`}>{message}</li>
                        ))}
                     </ul>
                  )}
               </div>
            </MITFloatingPortal>
         ) : null}
      </>
   );
}
